import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { DataGridPro, koKR } from '@mui/x-data-grid-pro';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DialogTitleClose, PaperComponent } from "../dialog";
import { CustomNoRowsOverlay } from "../datagrid";
import dayjs from 'dayjs';
import * as gclientPaymentsActions from "../../store/gclientPayments";
import { PARTNERSHIP_TYPE, MEMBERSHIP_TYPE, MEMBERSHIP_TYPE_LABELS } from '../../constants/gclientTypes';


const PaymentHistoryDialog = ({ open, setOpen }) => {
  const [loaded, setLoaded] = useState(true);
  const sessionUser = useSelector(state => state.session.sessionUser);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const dispatch = useDispatch();
  const rows = useSelector((state) => state.gclientPayments.gclientPayments);

  const handleDialogClose = () => {
    setOpen(false);
  };

  const columns = [
    { 
      field: 'membership', 
      headerName: '서비스',
      width: 170,
      alignHeader: 'center',
      valueFormatter: (params) => {
        return params.value ? MEMBERSHIP_TYPE_LABELS[params.value] || params.value : '';
      }
    },
    { 
      field: 'payDate', 
      headerName: '납부일자',
      width: 100,
      valueFormatter: (params) => {
        return params.value ? dayjs(params.value).format('YYYY-MM-DD') : '';
      }
    },
    { 
      field: 'amount', 
      headerName: '납부금액',
      width: 80,
      align: 'right',
      valueFormatter: (params) => {
        return params.value ? params.value.toLocaleString() + '원' : '';
      }
    },
    { 
      field: 'startDate', 
      headerName: '시작일자',
      width: 100,
      valueFormatter: (params) => {
        return params.value ? dayjs(params.value).format('YYYY-MM-DD') : '';
      }
    },
    { 
      field: 'endDate', 
      headerName: '종료일자',
      flex: 1,
      minWidth: 100,
      valueFormatter: (params) => {
        return params.value ? dayjs(params.value).format('YYYY-MM-DD') : '';
      }
    }
  ];

  useEffect(() => {
    if (open && sessionUser) {
        setLoaded(false);
        dispatch(gclientPaymentsActions.selectAll(sessionUser.id, isMobile ? { size: 2000 } : undefined))
        .then((response) => {
        console.log(response);
        })
        .catch(error => {
        console.error('목록 조회 실패:', error);
        setLoaded(true);
        })
        .finally(() => {
        setLoaded(true);
        });
    }
  }, [open, sessionUser, isMobile]);
  

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      PaperComponent={isMobile ? undefined : PaperComponent}
      PaperProps={isMobile ? { sx: { display: 'flex', flexDirection: 'column' } } : undefined}
      fullScreen={isMobile}
      aria-labelledby="draggable-dialog-title"
      maxWidth="sm"
      fullWidth={!isMobile}
    >
      <DialogTitleClose
        id="draggable-dialog-title"
        onClose={handleDialogClose}
        style={isMobile ? undefined : { cursor: 'move' }}
      >
        {"납부이력"}
      </DialogTitleClose>
      <DialogContent sx={isMobile ? { flex: '1 1 auto', minHeight: 0 } : undefined}>
      {isMobile ? (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mb: 1 }}>
            {"[VAT포함]"}
          </Typography>
          {!loaded && <LinearProgress />}
          {loaded && (!Array.isArray(rows) || rows.length === 0) && (
            <Typography sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
              {"납부이력이 없습니다."}
            </Typography>
          )}
          {Array.isArray(rows) && rows.map((row, index) => {
            const service = row.membership ? (MEMBERSHIP_TYPE_LABELS[row.membership] || row.membership) : '';
            const payDate = row.payDate ? dayjs(row.payDate).format('YYYY-MM-DD') : '';
            const startDate = row.startDate ? dayjs(row.startDate).format('YYYY-MM-DD') : '';
            const endDate = row.endDate ? dayjs(row.endDate).format('YYYY-MM-DD') : '';
            const period = [startDate, endDate].filter(Boolean).join(' ~ ');
            return (
              <Box
                key={row.id || `${row.payDate}_${row.amount}_${index}`}
                sx={{
                  py: 1.25,
                  borderTop: index === 0 ? 'none' : '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>{service}</Typography>
                  <Typography sx={{ fontWeight: 700, flexShrink: 0 }}>
                    {row.amount ? `${Number(row.amount).toLocaleString()}원` : ''}
                  </Typography>
                </Box>
                {payDate && (
                  <Typography variant="body2" color="text.secondary">{`납부 ${payDate}`}</Typography>
                )}
                {period && (
                  <Typography variant="caption" color="text.secondary">{period}</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      ) : (
      <Box sx={{ width: '100%', height: 200, position: 'relative' }}>
        <Box 
        sx={{ 
            position: 'absolute', 
            top: -20,           
            right: 0,        
            fontSize: '0.75em',
            color: '#666',
            backgroundColor: 'transparent'
        }}
        >
        [VAT포함]
        </Box>
        <Box sx={{ 
        width: '100%', 
        height: '100%',
        mt: 4  // VAT포함 텍스트 아래로 DataGrid 이동
        }}>
      <DataGridPro
        localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
        sx={{ 
          cursor: 'pointer', 
          fontSize: '0.85em',
          '& .MuiDataGrid-cell': {
            padding: '8px'
          },
          '& .MuiDataGrid-columnHeader': {  // 헤더 중앙정렬을 위한 스타일 추가
            justifyContent: 'center',
            '.MuiDataGrid-columnHeaderTitle': {
              textAlign: 'center'
            }
          }
        }}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
          loadingOverlay: LinearProgress,
        }}
        headerHeight={38}
        rowHeight={34}
        rows={rows}
        columns={columns}
        loading={!loaded}
        hideFooter={true} 
        getRowId={(row) => row.id || `${row.paymentDate}_${row.amount}`}
      />
    </Box>
  </Box>
      )}
      </DialogContent>
      <DialogActions {...(isMobile ? {
        sx: {
          '&&': {
            justifyContent: 'stretch',
            padding: '12px 16px calc(12px + env(safe-area-inset-bottom)) !important',
          },
          '& .MuiButton-root': {
            flex: 1,
            height: 48,
            marginLeft: '0 !important',
            fontSize: '1rem',
          },
        },
      } : {})}>
        <Button variant={isMobile ? 'outlined' : 'text'} onClick={handleDialogClose}>{"닫기"}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentHistoryDialog;